/**
 * The one answer to "what kind of environmentId is this".
 *
 * Forge used to answer it from three predicates in two files, plus an inline
 * check in a fourth, and nothing steered a call site to the right one. The
 * narrowest (`isCmmdRuntimeEnvironmentId`) was imported at twice the sites of
 * the broad one most of them needed, so a durable `env_…` id fell through cloud
 * detection twice in production: PR #3333 (the two-clouds composer dropdown)
 * and FOR-36826 (a sleeping durable Environment connecting through the
 * non-renewing path). Each was a one-line fix. The defect was that there were
 * three places to fix. `docs/adr/one-module-classifies-environment-ids.md`
 * records the decision and
 * `docs/specs/2026-09-16-environment-id-classification-spec.md` the migration.
 *
 * The shape table, decided here and nowhere else:
 *
 * | shape                       | matches                                  | isCloud | legacyRuntimeId |
 * | --------------------------- | ---------------------------------------- | ------- | --------------- |
 * | `legacy-runtime`            | `cmmd-runtime:` + a well-formed runtimeId| true    | the runtimeId   |
 * | `cloud-project-placeholder` | the `cmmd-cloud-project:` prefix         | true    | null            |
 * | `durable-environment`       | `env_` + exactly 32 lowercase hex        | true    | null            |
 * | `local`                     | anything else, a bare uuid included      | false   | null            |
 *
 * A `cmmd-runtime:` id whose tail is missing or malformed classifies as
 * `local`. It names no runtime, and every caller that once accepted it on a
 * bare prefix check went on to extract an empty runtime id that routed
 * nowhere. Rejecting it here is the stricter of the two behaviours the old
 * predicates disagreed on, and the one the old narrow predicate already had.
 * The placeholder is checked by prefix alone, as it always was: it is built in
 * one place client-side and never addresses anything, so a malformed tail has
 * nowhere to misroute to.
 *
 * Questions this module refuses to answer, on purpose:
 *
 * - Which runtime currently backs a durable `env_` Environment. A repair can
 *   rotate the runtime without rotating the Environment id, so no string check
 *   can know. That is an async catalog lookup with its own failure modes
 *   (`resolveCloudEnvironmentId` in `threadExecutionIntentEnvironmentApi.ts`).
 * - Whether a saved environment is a Credential Vault. That is
 *   `SavedEnvironmentKind` (`"project" | "auth"`) on the persisted record, a
 *   field orthogonal to id shape.
 *
 * Open question, deliberately left open: `addProjectSources.ts` caps
 * `CMMD_RUNTIME_PROJECT_CREATE_MAX_ATTEMPTS` per legacy runtime id. Whether
 * that cap should apply to every cloud shape or stay runtime-scoped is a
 * product call the repo owner asked to make once the migration lands. Do not
 * settle it in passing while touching that file.
 *
 * The legacy prefix is imported from `cmmdRuntimeEnvironmentId.ts` rather than
 * spelled here: that module's test enforces that it is the only file defining
 * the literal. When FOR-32652 retires the format, that module goes and so does
 * the `legacy-runtime` branch below.
 */

import { CMMD_RUNTIME_ENVIRONMENT_PREFIX } from "./cmmdRuntimeEnvironmentId";

/**
 * Namespace for a cloud project that is registered but has no runtime yet.
 *
 * There is nothing to address: no VM, no websocket, no workspace. The row still
 * needs a stable identity to render, sort and persist expansion state under.
 * Deliberately not the legacy runtime prefix, because every runtime-addressing
 * path keys off that one and would be handed an id that resolves to nothing.
 * Constructed only by `resolveCloudProjectEnvironmentId` in
 * `cloudProjectsGroup.logic.ts`.
 */
export const CMMD_CLOUD_PROJECT_ENVIRONMENT_PREFIX = "cmmd-cloud-project:";

/**
 * CMMD's durable Environment identifier (FOR-32643). Mirrors
 * `ENVIRONMENT_ID_PATTERN` in CMMD's `forge-runtime-identity.ts`. Issued by
 * the server, never constructed client-side.
 */
const CMMD_DURABLE_ENVIRONMENT_ID_PATTERN = /^env_[0-9a-f]{32}$/;

/**
 * What may follow the legacy prefix and still name a runtime: an alphanumeric
 * opener, then alphanumerics, dots, underscores or hyphens. An empty tail,
 * whitespace, a path or a leading separator all fail it.
 */
const LEGACY_RUNTIME_ID_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._-]*$/;

export type EnvironmentIdShape =
  | "legacy-runtime"
  | "cloud-project-placeholder"
  | "durable-environment"
  | "local";

export interface EnvironmentIdClassification {
  /** Which row of the shape table matched. Reach for `isCloud` first; this is for the rare durable-only question. */
  readonly shape: EnvironmentIdShape;
  /** Does this row live in the cloud, whether or not a VM is behind it right now. */
  readonly isCloud: boolean;
  /** The runtime id the string itself encodes. Non-null only for the legacy shape. */
  readonly legacyRuntimeId: string | null;
}

const LOCAL: EnvironmentIdClassification = {
  shape: "local",
  isCloud: false,
  legacyRuntimeId: null,
};

const CLOUD_PROJECT_PLACEHOLDER: EnvironmentIdClassification = {
  shape: "cloud-project-placeholder",
  isCloud: true,
  legacyRuntimeId: null,
};

const DURABLE_ENVIRONMENT: EnvironmentIdClassification = {
  shape: "durable-environment",
  isCloud: true,
  legacyRuntimeId: null,
};

/**
 * Pure and synchronous. Accepts `unknown` because the predicates it replaces
 * did, and their call sites hand it `EnvironmentId | undefined` and friends;
 * anything that is not a string is `local`.
 */
export function classifyEnvironmentId(environmentId: unknown): EnvironmentIdClassification {
  if (typeof environmentId !== "string") return LOCAL;

  if (environmentId.startsWith(CMMD_RUNTIME_ENVIRONMENT_PREFIX)) {
    const runtimeId = environmentId.slice(CMMD_RUNTIME_ENVIRONMENT_PREFIX.length);
    if (!LEGACY_RUNTIME_ID_PATTERN.test(runtimeId)) return LOCAL;
    return { shape: "legacy-runtime", isCloud: true, legacyRuntimeId: runtimeId };
  }

  if (environmentId.startsWith(CMMD_CLOUD_PROJECT_ENVIRONMENT_PREFIX)) {
    return CLOUD_PROJECT_PLACEHOLDER;
  }

  if (CMMD_DURABLE_ENVIRONMENT_ID_PATTERN.test(environmentId)) return DURABLE_ENVIRONMENT;

  return LOCAL;
}
